<script setup lang="ts">
import { ExtendedMonthlyPurchase, ExtendedMonthlyTransfer } from "../../types/PersologOnlineTypes.ts";
import { useI18n } from "vue-i18n";
import { computed, onMounted, PropType, ref } from "vue";
import { filenameFromTimeRange, formatDate } from "../../utils/GeneralUtils.ts";
import { FilterMatchMode } from "@primevue/core/api";
import TooltipButton from "../common/TooltipButton.vue";

const { t } = useI18n();

const props = defineProps({
    transactions: {
        type: Object as PropType<ExtendedMonthlyTransfer[] | ExtendedMonthlyPurchase[]>,
        required: true
    },
    direction: {
        type: String as () => "transferred" | "received",
        default: "transferred"
    },
    headline: {
        type: String,
        required: true
    },
    tooltipMessage: {
        type: String,
        default: ""
    },
    type: {
        type: String as () => "purchase" | "transfer",
        required: true
    },
    withIcon: {
        type: Boolean,
        default: false
    },
    withSource: {
        type: Boolean,
        default: false
    },
    displayOwnOption: {
        type: Boolean,
        default: false
    },
    withOwnPurchases: {
        type: Object as PropType<{ name: string; value: number }>,
        default: () => (
            { name: "", value: 1 }
        )
    },
    showTotalQuantity: {
        type: Boolean,
        default: true
    },
    totalQuantityOverride: {
        type: Number,
        default: null
    },
    // Renders a per-row reclaim action (RDT-113) for rows that carry a reclaimable trainerId,
    // i.e. sub-account acquisitions in the distributor's purchases table.
    withReclaim: {
        type: Boolean,
        default: false
    }
});

const ownPurchases = ref();

const filters = ref({
    productDescription: { value: null, matchMode: FilterMatchMode.CONTAINS },
    recipient: { value: null, matchMode: FilterMatchMode.CONTAINS },
    trainer: { value: null, matchMode: FilterMatchMode.CONTAINS },
    dateDisplay: { value: null, matchMode: FilterMatchMode.CONTAINS },
    manual: { value: null, matchMode: FilterMatchMode.EQUALS },
    source: { value: null, matchMode: FilterMatchMode.CONTAINS }
});

// The date column filters and exports on the local-time text the user sees,
// while sorting stays on the raw UTC field for chronological order.
const displayedTransactions = computed(() =>
    props.transactions.map((transaction) => ({
        ...transaction,
        dateDisplay: formatDate(transaction.date, t("formats.datetime")),
    }))
);

const types = [
    { label: t("header.accountSettings.transactionTable.tooltips.purchased"), value: true },
    { label: t("header.accountSettings.transactionTable.tooltips.received"), value: false }
];

const options = ref([
    { name: t("header.accountSettings.distributor.own.purchases.own"), value: 1 },
    { name: t("header.accountSettings.distributor.own.purchases.subs"), value: 2 },
    { name: t("header.accountSettings.distributor.own.purchases.all"), value: 3 }
]);

const filteredTransactions = ref<ExtendedMonthlyTransfer[] | ExtendedMonthlyPurchase[] | null>(null);

function onFilter(event: any) {
    filteredTransactions.value = event.filteredValue || null;
}

const emits = defineEmits(["own-purchases", "page-change", "reclaim"]);

const dtPurchase = ref();
const dtTransferred = ref();
const dtReceived = ref();

const rows = ref(5);
const showPaginator = computed(() => props.transactions.length > 0);
const hasActiveFilters = computed(() =>
    Object.values(filters.value).some((filter) => filter.value !== null && filter.value !== "")
);
const displayedTotalQuantity = computed(() => {
    // The override carries the unfiltered total (e.g. the backend range total when rows are loaded
    // page by page). As soon as a filter is active, the sum of the visible rows is what counts.
    if (!hasActiveFilters.value && props.totalQuantityOverride !== null) {
        return props.totalQuantityOverride;
    }

    return (filteredTransactions.value ?? props.transactions).reduce(
        (sum, transaction) => sum + transaction.quantity,
        0
    );
});

/**
 * The distributor purchases table can be filtered to own / sub-account / all purchases. Reflect
 * the active filter in the export file name instead of the generic table headline; "all" keeps
 * the generic headline. All other tables just use their headline.
 */
const exportName = computed(() => {
    if (props.type !== "purchase" || !props.displayOwnOption) {
        return props.headline;
    }
    const mode = ownPurchases.value?.value ?? props.withOwnPurchases?.value;
    if (mode === 1) {
        return t("header.accountSettings.distributor.own.purchases.own");
    }
    if (mode === 2) {
        return t("header.accountSettings.distributor.own.purchases.subs");
    }
    return props.headline;
});

const exportfilename = computed(() =>
    filenameFromTimeRange(
        exportName.value,
        props.transactions[0]?.date,
        props.transactions[props.transactions.length - 1]?.date
    )
);

const exportCSV = () => {
    if (props.type === "purchase") {
        dtPurchase.value.exportCSV();
    } else if (props.direction === "received") {
        dtReceived.value.exportCSV();
    } else {
        dtTransferred.value.exportCSV();
    }
};

function getRef() {
    if (props.type === "purchase") {
        return dtPurchase;
    } else if (props.direction === "received") {
        return dtReceived;
    } else {
        return dtTransferred;
    }
}

function toggle(currentState: { name: string, value: number }) {
    emits("own-purchases", currentState);
}

function onPageChange(event: { first: number; rows: number; page: number; pageCount: number }) {
    emits("page-change", event);
}

/**
 * Label for the balance-activity column: manual rows are shown as purchased, all others as received.
 */
function balanceActivityLabel(manual: boolean): string {
    if (manual) {
        return t("header.accountSettings.transactionTable.tooltips.purchased");
    }
    return t("header.accountSettings.transactionTable.tooltips.received");
}

const escapeCsv = (value: unknown) => String(value ?? "").replace(/"/g, '""');

/**
 * The recipient/trainer field carries "Name (email)" so the on-screen filter, sort and column
 * text all match. The purchases CSV has a dedicated e-mail column, so drop a trailing " (…@…)"
 * there to leave just the name.
 */
const nameWithoutEmail = (value: unknown) =>
    String(value ?? "").replace(/\s*\([^()]*@[^()]*\)\s*$/, "");

/**
 * The balance-activity column stores the `manual` boolean, which PrimeVue would export as
 * TRUE/FALSE. Export the same localized label shown in the table; the trainer name is exported
 * without its e-mail (the purchases table has a separate e-mail column); every other column
 * keeps its raw value, escaped like PrimeVue's default export.
 */
const exportFunction = (event: { data: unknown; field: string }) => {
    if (event.field === "manual") {
        return escapeCsv(balanceActivityLabel(Boolean(event.data)));
    }
    if (props.type === "purchase" && (event.field === "recipient" || event.field === "trainer")) {
        return escapeCsv(nameWithoutEmail(event.data));
    }
    if (event.field === "date") {
        return escapeCsv(formatDate(String(event.data), t("formats.datetime")));
    }
    return escapeCsv(event.data);
};

onMounted(() => {
    ownPurchases.value = props.withOwnPurchases;
});
</script>

<template>
    <DataTable
        v-model:filters="filters"
        filter-display="row"
        :ref="getRef()"
        :value="displayedTransactions"
        :id="'transfer-table_data-table_' + type + '-' + direction"
        :paginator="showPaginator"
        :rows="rows"
        :rows-per-page-options="[5, 10, 20, 50]"
        :style="{ minWidth: '100%' }"
        :export-filename="exportfilename"
        :export-function="exportFunction"
        @filter="onFilter"
        @page="onPageChange"
    >
        <template #header v-if="headline">
            <div class="flex justify-between items-center">
                <div class="flex items-center gap-2">
                    <h3 class="m-0">{{ headline }}</h3>
                    <TooltipButton
                        v-if="tooltipMessage"
                        :content="tooltipMessage"
                        background-color="white"
                        
                    />
                </div>
                <div v-if="displayOwnOption">
                    <SelectButton
                        id="transfer-table_select-button_purchases"
                        v-model="ownPurchases"
                        :options="options"
                        option-label="name"
                        :allow-empty="false"
                        @click="toggle(ownPurchases)"
                    />
                </div>
            </div>
        </template>

        <Column field="productDescription" :header="t('header.accountSettings.transactionTable.product')" sortable>
            <template #filter="{ filterModel, filterCallback }">
                <InputText v-model="filterModel.value" type="text" @input="filterCallback()"
                           :placeholder="t('fields.search')" />
            </template>
        </Column>
        <Column field="quantity" :header="t('header.accountSettings.transactionTable.quantity')" />
        <Column 
                v-if="withIcon" field="manual"
                :header="t('header.accountSettings.transactionTable.balanceActivityType')" sortable>
            <template #body="slotProps">
                <i
                    :class="`${slotProps.data.manual ? 'pi pi-shopping-cart' : 'pi pi-warehouse'} mr-2`"
                    v-tooltip="balanceActivityLabel(slotProps.data.manual)"
                />
                <span>{{ balanceActivityLabel(slotProps.data.manual) }}</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <Select
                    v-model="filterModel.value"
                    @update:model-value="(val) => {
                        filterModel.value = val === 'true' ? true : val === 'false' ? false : val;
                        filterCallback();
                    }"
                    :options="types"
                    option-label="label"
                    option-value="value"
                    :placeholder="t('fields.search')"
                    style="min-width: 12rem"
                    :show-clear="true"
                >
                    <template #option="slotProps">
                        <Tag :value="slotProps.option.label" />
                    </template>
                </Select>
            </template>
        </Column>
        <Column
            v-if="direction === 'received'"
            field="trainer"
            :header="t('header.accountSettings.transactionTable.trainer')"
            sortable
        >
            <template #body="slotProps">
                <span class="mr-2">{{ slotProps.data.trainer }}</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText v-model="filterModel.value" type="text" @input="filterCallback()"
                           :placeholder="t('fields.search')" />
            </template>
        </Column>
        <Column v-else field="recipient" :header="t('header.accountSettings.transactionTable.trainer')" sortable>
            <template #body="slotProps">
                <span class="mr-2">{{ slotProps.data.recipient }}</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText v-model="filterModel.value" type="text" @input="filterCallback()"
                           :placeholder="t('fields.search')" />
            </template>
        </Column>
        <!-- Export-only column: adds the trainer's e-mail as its own column in the purchases CSV. -->
        <Column
            v-if="type === 'purchase'"
            field="email"
            :header="t('header.accountSettings.transactionTable.email')"
            hidden
        />
        <Column v-if="withSource" field="source"
                :header="t('header.accountSettings.transactionTable.source')" sortable>
            <template #body="slotProps">
                <span>{{ slotProps.data.source || "—" }}</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText v-model="filterModel.value" type="text" @input="filterCallback()"
                           :placeholder="t('fields.search')" />
            </template>
        </Column>
        <Column field="date" filter-field="dateDisplay" :header="t('header.accountSettings.transactionTable.date')" sortable>
            <template #body="slotProps">
                {{ slotProps.data.dateDisplay }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText v-model="filterModel.value" type="text" @input="filterCallback()"
                   :placeholder="t('fields.search')" />
            </template>
        </Column>
        <Column v-if="withReclaim" :exportable="false" :header="t('header.accountSettings.accountLinks.actions')">
            <template #body="slotProps">
                <Button
                    v-if="slotProps.data.trainerId"
                    :id="'transfer-table_btn_reclaim_' + slotProps.data.id"
                    icon="pi pi-history"
                    v-tooltip="t('header.accountSettings.accountLinks.tooltips.reclaim')"
                    rounded
                    outlined
                    @click="emits('reclaim', slotProps.data)"
                ></Button>
            </template>
        </Column>

        <template #paginatorstart>
            <template v-if="showTotalQuantity">
                <strong>{{ t("common.total.quantity") }}:</strong>
                <Tag class="ml-2"
                     :id="'transfer-table_tag_total-quantity_' + type + '-' + direction"
                     :value="displayedTotalQuantity" />
            </template>
            <template v-else>
                &nbsp;
            </template>
        </template>
        <template #paginatorend>
            <Button
                icon="pi pi-download"
                text
                :label="t('buttons.export')"
                size="small"
                @click="exportCSV()"
                :disabled="transactions.length == 0"
            />
        </template>
    </DataTable>
</template>
